import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import ClassNames from "classnames";
import TextInput from "../../controls/TextInput/TextInput.js";
import SidebarCategory from "./SidebarCategory";
import SidebarTitleActions from "./SidebarTitleActions";
import type { ActionsMenuItem } from "../ActionsMenu/ActionsMenu";
import type { DataAnalyticsAttributes } from "../../../utils/UserAnalyticsUtils";
import "./Sidebar.scss";

export interface SidebarSubItem extends DataAnalyticsAttributes {
  title: string;
  path: string;
  /** Must be a static, whitelisted seem-icon-* constant — never a dynamic string from backend/untrusted data (it is interpolated into a className, so a dynamic value is a CSS class-injection vector). */
  icon?: string;
  iconTooltip?: string;
  actions?: ActionsMenuItem[];
  dataTestId?: string;
}

export interface SidebarMenuItem extends DataAnalyticsAttributes {
  /**
   * The category header. A category without one renders its items as a flat,
   * un-indented list with no header and no collapse toggle, for a menu that is
   * a single list rather than a set of groups.
   */
  title?: string;
  items: SidebarSubItem[];
  /** Must be a static, whitelisted seem-icon-* constant — never a dynamic string from backend/untrusted data (it is interpolated into a className, so a dynamic value is a CSS class-injection vector). */
  icon?: string;
  collapsable?: boolean;
  defaultExpanded?: boolean;
  /**
   * Shown in place of the items when the category has none of its own. A
   * category without one stays silent when empty, which is what a group of a
   * larger menu wants; `emptySearchLabel` still covers the search case.
   */
  emptyLabel?: string;
  headerActions?: React.ReactNode;
}

export interface SidebarProps {
  menuItems: SidebarMenuItem[];
  title: string;
  titleImg?: React.ReactNode;
  titleActions?: ActionsMenuItem[];
  actionsComponent?: React.ReactNode;
  /** Rendered between the header and the search box, for controls that scope the menu below. */
  filtersComponent?: React.ReactNode;
  searchable?: boolean;
  searchPlaceholder?: string;
  emptySearchLabel?: string;
  className?: string;
  "data-testid"?: string;
  searchDataTestId?: string;
}

const Sidebar: React.FC<SidebarProps> = ({
  menuItems,
  title,
  titleImg,
  titleActions,
  actionsComponent,
  filtersComponent,
  searchable = false,
  searchPlaceholder = "Search",
  emptySearchLabel = "No results match your search",
  className,
  "data-testid": dataTestId = "sidebar",
  searchDataTestId,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const { pathname } = useLocation();

  return (
    <div className={ClassNames("sidebar", className)} data-testid={dataTestId}>
      <div className="sidebar-header">
        <div className="sidebar-title-container">
          {!!titleImg && <div className="sidebar-title-img">{titleImg}</div>}
          <h2 className="sidebar-title">{title}</h2>
          {!!titleActions?.length && (
            <SidebarTitleActions
              actions={titleActions}
              data-testid={dataTestId}
            />
          )}
        </div>
        {!!actionsComponent && (
          <div className="sidebar-actions">{actionsComponent}</div>
        )}
      </div>

      {!!filtersComponent && (
        <div className="sidebar-filters">{filtersComponent}</div>
      )}

      {searchable && (
        <TextInput
          className="sidebar-search no-padding-bottom"
          data-testid={searchDataTestId ?? `${dataTestId}-search`}
          leftIcon="seem-icon-search"
          isClearable
          value={searchValue}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setSearchValue(e.target.value)
          }
          placeholder={searchPlaceholder}
        />
      )}

      <nav className="sidebar-menu">
        {menuItems.map((category, index) => (
          <SidebarCategory
            key={`${category.title ?? ""}-${index}`}
            category={category}
            searchValue={searchValue}
            emptySearchLabel={emptySearchLabel}
            activePath={pathname}
            data-testid={dataTestId}
          />
        ))}
      </nav>
    </div>
  );
};

export default Sidebar;
